import {Injectable} from '@angular/core';
import {ApiResponse, ApiService} from './api/api.service';
import {TtmType} from '../classes/ttmtype';
import {TtmStatus} from '../classes/ttmstatus';
import {Ttm} from '../classes/ttm';
import {EntityImage} from '../classes/entityimage';
import {shareReplay} from 'rxjs/operators';
import {Entity} from '../classes/entity';
import {Observable} from 'rxjs';
import {Utils} from '../utils.class';
import {TtmHistory} from '../components/ttm-history-dialog/ttm-history-dialog.component';

@Injectable({
    providedIn: 'root'
})
export class TtmService {

    private _types$: Observable<ApiResponse<TtmType[]>>;
    private _statuses$: Observable<ApiResponse<TtmStatus[]>>;

    public static lineTypeNameMap = new Map<string, string>([
        ['39strp', 'markering 3-9 Streep'],
        ['93strp', 'markering 9-3 Streep'],
        ['11strp', 'markering 1-1 Streep'],
        ['13strp', 'markering 1-3 Streep'],
        ['91strp', 'markering 9-1 Streep'],
        ['markering 0.5-0.5 Streep', 'markering 0.5-0.5 Streep'],
        ['markering 0.50-0.50 Streep', 'markering 0.50-0.50 Streep'],
        ['markering 0.60-0.60 Streep', 'markering 0.60-0.60 Streep'],
        ['markering 1-1 Streep', 'markering 1-1 Streep'],
        ['1-1_streep', 'markering 1-1 Streep'],
        ['markering 1-3 Streep', 'markering 1-3 Streep'],
        ['1-3_streep', 'markering 1-3 Streep'],
        ['markering 3-1 Streep', 'markering 3-1 Streep'],
        ['3-1_streep', 'markering 3-1 Streep'],
        ['markering 3-9 Streep', 'markering 3-9 Streep'],
        ['3-9_streep', 'markering 3-9 Streep'],
        ['markering 9-1 Streep', 'markering 9-1 Streep'],
        ['9-1_streep', 'markering 9-1 Streep'],
        ['markering 9-3 Streep', 'markering 9-3 Streep'],
        ['9-3_streep', 'markering 9-3 Streep'],
        ['markering 30-270 Streep', 'markering 30-270 Streep'],
        ['markering 270-30 Streep', 'markering 270-30 Streep'],
        ['markering ononderbroken', 'markering ononderbroken'],
        ['vlak', 'markering vlakken']
    ]);

    constructor(private apiService: ApiService) {
    }

    types() {
        return this._types$ ??= this.apiService.getCall$<TtmType[]>(`ttms/types`).pipe(shareReplay());
    }

    statuses() {
        return this._statuses$ ??= this.apiService.getCall$<TtmStatus[]>(`ttms/statuses`).pipe(shareReplay());
    }

    merge(ttmId: number, mergeWith: number[]) {
        return this.apiService.postCall$<Ttm>(`ttms/merge`, {
            ttmId, mergeWith
        });
    }
    split(ttmId: number, keepEntities: number[]) {
        return this.apiService.postCall$<Ttm[]>(`ttms/split`, {
            ttmId, keepEntities
        });
    }

    findReplacementTtm(oldId: number) {
        return this.apiService.getCall$<Ttm>(`ttms/find-replacement-ttm/${oldId}`);
    }

    delete(id: number) {
        return this.apiService.deleteCall$<boolean>(`ttms/${id}`);
    }

    updateStatus(ttmId: number, status: string, position?: { lat: number; lng: number }) {
        return this.apiService.postCall$<string>(`ttms/update-status`, {ttmId, status, position});
    }

    updateAmount(ttmId: number, amount: number) {
        return this.apiService.postCall$<Ttm>(`ttms/update-amount`, {ttmId, amount});
    }

    updateWeighting(ttmId: number, field: string, value: boolean) {
        return this.apiService.postCall$<Ttm>(`ttms/update-weighting`, {ttmId, field, value});
    }

    updateComment(ttmId: number, comment: {
        comment: string,
        comment_show_on_status_change: boolean
    }) {
        return this.apiService.postCall$<Ttm>(`ttms/update-comment`, {ttmId, ...comment});
    }

    addCustomTtm(projectId: number, drawingIds: number[], location: { lat: number; lng: number }, typeKey: string) {
        return this.apiService.postCall$<Ttm>(`ttms/add`, {
            project_id: projectId,
            drawing_ids: drawingIds,
            lat: location.lat,
            lng: location.lng,
            type_key: typeKey
        });
    }

    regeneratePlainImage(ttmId: number) {
        return this.apiService.getCall$<EntityImage>(`ttms/regenerate-image/${ttmId}`);
    }

    getTtms(drawingId?: number, workorderId?: number) {
        return this.apiService.getCall$<{
            ttms: Ttm[],
            entities: Entity[]
        }>(`ttms`, Utils.removeEmptyProps({drawingId, workorderId}));
    }

    getTtmsImages(drawingId?: number, workorderId?: number) {
        return this.apiService.getCall$<EntityImage[]>(`ttms/images`, Utils.removeEmptyProps({drawingId, workorderId}));
    }

    getTtmHistory(ttmId: number) {
        return this.apiService.getCall$<TtmHistory[]>(`ttms/history/${ttmId}`);
    }

}
